import { FunctionComponent, useEffect, useMemo, useState, MutableRefObject } from "react";

import { Information } from "@/components/Information/Information";
import { LinkedinEditor } from "@/pages/Private/pages/Account/components/LinkedinEditor";

import { ColumnConfig } from "../../../schema/columnConfig";
import { EventOutreachSchemaType } from "../../../schema/list";

import {
	EmailNodeConfig,
	LinkedinInmailNodeConfig,
	LinkedinInviteNodeConfig,
	LinkedinNodeConfig,
} from "./nodes";

import { MyNode } from "../CampaignOverview";

import { Sender } from "./Sender";
import { LinkedinVisitNodeConfig } from "./nodes/LinkedinVisitNode";

export const LinkedinPremiumInvite: FunctionComponent<{
	currentLeadIndex: number;
	setSelectedNode: (node: EmailNodeConfig) => void;
	selectedNode:
		| MyNode
		| EmailNodeConfig
		| LinkedinInviteNodeConfig
		| LinkedinNodeConfig
		| LinkedinInmailNodeConfig
		| LinkedinVisitNodeConfig
		| null;
	nodesRef: MutableRefObject<MyNode[]>;
	sender?: Sender;
	eventBasedState: boolean;
	setIsDirty?: (isDirty: boolean) => void;
	onUpdate?: () => void;
	listColumns?: ColumnConfig[];
	listEvent?: EventOutreachSchemaType | null;
	listId?: number;
	onCreateSpintax?: () => void;
	onCreateAiSnippet?: () => void;
	showAiSnippetButton?: boolean;
}> = ({
	currentLeadIndex,
	setSelectedNode,
	selectedNode,
	nodesRef,
	sender,
	eventBasedState,
	setIsDirty,
	onUpdate,
	listColumns,
	listEvent,
	listId,
	onCreateSpintax,
	onCreateAiSnippet,
	showAiSnippetButton,
}) => {
	const currentValue = useMemo(() => {
		return eventBasedState
			? (selectedNode?.data.alternatePremiumMessage ?? "")
			: (selectedNode?.data.premiumMessage ?? "");
	}, [selectedNode, eventBasedState]);

	const updateText = (value: string) => {
		if (eventBasedState) {
			setSelectedNode({
				...selectedNode,
				data: {
					...selectedNode?.data,
					alternatePremiumMessage: value,
				},
			} as EmailNodeConfig);
		} else {
			setSelectedNode({
				...selectedNode,
				data: {
					...selectedNode?.data,
					premiumMessage: value,
				},
			} as EmailNodeConfig);
		}

		nodesRef.current = nodesRef.current.map((node) => {
			if (node.id === selectedNode?.id) {
				if (eventBasedState) {
					return { ...node, data: { ...node.data, alternatePremiumMessage: value } };
				} else {
					return { ...node, data: { ...node.data, premiumMessage: value } };
				}
			}

			return node;
		});

		if (
			(!eventBasedState && value !== selectedNode?.data.premiumMessage) ||
			(eventBasedState && value !== selectedNode?.data.alternatePremiumMessage)
		) {
			setIsDirty?.(true);
			onUpdate?.();
		}
	};

	return (
		<>
			<div className="text-sm whitespace-nowrap flex flex-col gap-1 items-start mr-4 mb-2 mt-2">
				<div className="text-sm whitespace-nowrap flex flex-col gap-1 items-start mr-4 mb-2 mt-4">
					<div>
						<div className="flex flex-row items-center gap-1">
							As Linkedin Premium & Sales Navigator User{" "}
							<Information
								information={
									"Linkedin Premium Users or Sales Navigator users can add notes to all invites."
								}
							/>
						</div>
					</div>
					<div className="flex flex-row items-center">
						Add a note to your invitation (max. 300 characters){" "}
					</div>
				</div>
			</div>

			<LinkedinEditor
				currentLeadIndex={currentLeadIndex}
				handleChange={(value: string) => {
					updateText(value);
				}}
				handleInputChange={() => {
					setIsDirty?.(true);
				}}
				listColumns={listColumns}
				listEvent={listEvent}
				listId={listId}
				maxLength={300}
				placeholder="Write your message here"
				sender={sender}
				showAiSnippetButton={showAiSnippetButton}
				signature={currentValue}
				onCreateAiSnippet={onCreateAiSnippet}
				onCreateSpintax={onCreateSpintax}
			/>
		</>
	);
};
